import { Col, Form, Input, Radio, Row, Select, Space, Typography } from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { CiLocationOn } from 'react-icons/ci';
import { GrMapLocation } from 'react-icons/gr';

import AddressUnitsSelector from '../../../../components/common/AddressUnitSelector';
import SectionCard from '../../../../components/sectionCard/SectionCard';
import StepFooter from '../../../../components/stepFooter/StepFooter';
import { AddOrderReturnContext } from '../addOrderReturnContext';
import { getCustomField } from '../Step1/helper';

const { TextArea } = Input;
const { Text } = Typography;

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/** Сонголтын картын хамгийн их өргөн — хоёр карт зэрэгцэж багтана */
const OPTION_MAX_WIDTH = 420;

/** Бараа авах огнооны талбар — `getCustomField`-ээр рендерлэгдэнэ */
const PICKUP_DATE_FIELD = [
  { key: 'pickupDate', name: 'Бараа авах огноо', instance: 'date', required: true, order: 1 },
];

/**
 * Радио сонголтын карт — сонгогдсон үед хүрээ, дэвсгэрээрээ ялгарна.
 *
 * `height: 100%` нь баганаа дүүргэнэ: тайлбарын урт өөр байсан ч бүх карт
 * мөрийн хамгийн өндөртэй нь тэнцэнэ (`Row align="stretch"`-тэй хослоно).
 */
const optionCardStyle = (checked) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: OPTION_MAX_WIDTH,
  height: '100%',
  margin: 0,
  padding: 12,
  border: `1px solid ${checked ? '#4bbeda' : '#dcdfe4'}`,
  backgroundColor: checked ? '#f1fafd' : '#fff',
  borderRadius: 8,
  transition: 'border-color 0.12s ease, background-color 0.12s ease',
});

const DELIVERY_TYPE_TEXT = {
  selfReturn: {
    title: 'Өөрөө хүргэж өгөх',
    description: 'Буцаалтын барааг хэрэглэгч агуулахад өөрөө хүргэж өгөх захиалга',
  },
  default: {
    title: 'Гараас авах',
    description: 'Буцаалтын барааг хэрэглэгчийн гараас авах захиалга',
  },
};

const Step2 = ({ nextStep, prevStep }) => {
  const [form] = Form.useForm();
  const deliveryType = Form.useWatch('deliveryType', form);

  const { reference, setFormDataStep2, products, formDataStep2, selectedOrder } =
    useContext(AddOrderReturnContext);

  const [warehouses, setWarehouses] = useState([]);
  const [locationLabel, setLocationLabel] = useState();
  const [addressArray, setAddressArray] = useState();
  const [customFields, setCustomFields] = useState(PICKUP_DATE_FIELD);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const contractType = reference?.merchantContract?.data?.contractType;

  useEffect(() => {
    switch (contractType) {
      case 'W':
      case 'WO':
        form.setFieldsValue({ returnPlace: 'Агуулах' });
        setWarehouses(reference?.warehouses ?? []);
        setLocationLabel('Агуулахын байршил');
        break;
      case 'O':
        form.setFieldsValue({ returnPlace: 'Салбар' });
        setLocationLabel('Салбарын байршил');
        setBranchesLoading(true);
        axios
          .post('/api/v1/me/branches', { status: 'A' })
          .then((res) => setWarehouses(res.data.result.docs ?? []))
          .catch((e) => {
            console.error('me/branches: ', e);
          })
          .finally(() => setBranchesLoading(false));
        break;
      default:
        break;
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (formDataStep2?.recieveAddress) {
      setAddressArray(formDataStep2.recieveAddress);
      return;
    }

    const address = selectedOrder?.dropoffAddressArray ?? [];
    if (address?.length > 2) {
      form.setFieldsValue({
        aimagHot: address[0].name ?? '',
        sumDuureg: address[1].name ?? '',
        bagHoroo: address[2].name ?? '',
      });
      setAddressArray(address);
    }
    //eslint-disable-next-line
  }, [selectedOrder, form]);

  const getRequiredRule = (fieldName) => ({
    required: true,
    message: `${fieldName} дутуу байна!`,
  });

  const onChangeDeliveryType = (e) => {
    const type = e.target.value;
    form.setFieldsValue({
      deliveryPositionCode: undefined,
      pricingCode: undefined,
      dropoffDate: undefined,
      dropoffTime: undefined,
    });

    // Өөрөө хүргэж өгөхөд дэлгэрэнгүй хаяг нь тогтмол
    if (type === 'selfReturn') form.setFieldsValue({ dropoffDetail: 'Өөрөө хүргэж өгөх' });

    setCustomFields(PICKUP_DATE_FIELD);
  };

  const onFinish = (values) => {
    values.recieveAddress = addressArray;
    setFormDataStep2({
      ...values,
      formValues: form.getFieldsValue(),
      products,
      address: addressArray,
    });
    nextStep();
  };

  const warehouseOptions = warehouses.map((item) => ({
    value: item?.code,
    label: item?.name,
  }));

  const returnReasonOptions = (reference?.returnReason?.type ?? []).map((item) => ({
    value: item,
    label: reference?.returnReason?.detail?.[item] ?? item,
  }));

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ ...formDataStep2 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard icon={<GrMapLocation />} title="Тээвэр">
          <Form.Item
            name="deliveryType"
            rules={[{ required: true, message: 'Барааг хаанаас авахаа сонгоно уу!' }]}
            style={{ marginBottom: 0 }}
          >
            <Radio.Group onChange={onChangeDeliveryType}>
              {/* `stretch` + баганын `display: flex` — картууд ижил өндөр, өргөнтэй болно */}
              <Row gutter={[16, 12]} align="stretch">
                {reference?.deliveryType?.type?.map((item) => {
                  const text = DELIVERY_TYPE_TEXT[item] ?? DELIVERY_TYPE_TEXT.default;
                  return (
                    <Col xs={24} lg={12} key={item} style={{ display: 'flex' }}>
                      <Radio value={item} style={optionCardStyle(deliveryType === item)}>
                        <Space direction="vertical" size={2}>
                          <Text strong>{text.title}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {text.description}
                          </Text>
                        </Space>
                      </Radio>
                    </Col>
                  );
                })}
              </Row>
            </Radio.Group>
          </Form.Item>
        </SectionCard>

        <SectionCard icon={<CiLocationOn />} title="Буцаалтын мэдээлэл">
          <div style={{ maxWidth: FORM_MAX_WIDTH }}>
            {customFields.map((field) => getCustomField(field))}

            <AddressUnitsSelector
              selectedUnits={addressArray}
              onChange={setAddressArray}
              form={form}
              styleProps={{ rowGutter: [16, 0], columnSpans: [8, 8, 8] }}
              isErlian={deliveryType === 'selfReturn'}
            />

            <Row gutter={16}>
              <Col xs={24} xl={16}>
                <Form.Item
                  label="Дэлгэрэнгүй хаяг"
                  name="dropoffDetail"
                  rules={[{ required: true, message: 'Дэлгэрэнгүй хаягаа оруулна уу!' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Дэлгэрэнгүй хаяг" disabled={deliveryType === 'selfReturn'} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </SectionCard>

        <SectionCard icon={<AiOutlineInfoCircle />} title="Буцаах хаяг">
          <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item label="Хаашаа авах" name="returnPlace">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label={locationLabel}
                name="dropoffAddressCode"
                rules={[{ required: true, message: 'Бараа татах салбар сонгоогүй байна' }]}
              >
                <Select
                  placeholder="Сонгох"
                  options={warehouseOptions}
                  loading={branchesLoading}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Form.Item
                label="Буцаалтын шалтгаан"
                name="returnReason"
                rules={[getRequiredRule('Буцаалтын шалтгаан')]}
              >
                <Select
                  placeholder="Сонгох"
                  options={returnReasonOptions}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} xl={16}>
              <Form.Item
                label="Шалтгааны дэлгэрэнгүй"
                name="returnReasonDescription"
                rules={[getRequiredRule('Шалтгааны дэлгэрэнгүй')]}
                style={{ marginBottom: 0 }}
              >
                <TextArea rows={3} placeholder="Шалтгаанаа дэлгэрэнгүй бичнэ үү" />
              </Form.Item>
            </Col>
          </Row>
        </SectionCard>

        <StepFooter onPrev={prevStep} />
      </Space>
    </Form>
  );
};

export default Step2;
